import Event from "@cclib/classes/event.js";

/**
 * Merges a source event group and a target event group into a single set of
 * translation events.
 *
 * Timing and `text` come from the target events, `original` is the space joined text
 * of every source event that overlaps the target event. Source events matched by no
 * target event are appended with an empty `text` so nothing is lost.
 *
 * @param {object} groupA Source event group
 * @param {object} groupB Target event group
 * @returns {Array} merged events, sorted by start time
 */
export default function mergeTranslation(groupA, groupB) {
    const sourceEvents = groupA.events || [];
    const targetEvents = groupB.events || [];

    // Handle edge cases
    if (sourceEvents.length === 0) {
        // No source events, return target events with empty original
        return targetEvents.map(
            (event) => {
                return {...(new Event({
                    start: event.start,
                    end: event.end,
                    text: event.text || "",
                    original: "",
                }))}
            }
        );
    }

    if (targetEvents.length === 0) {
        // No target events, return source events with empty text
        return sourceEvents.map((event) => {
                return {...(new Event({
                        start: event.start,
                        end: event.end,
                        text: "",
                        original: event.text || "",
                    }))
                }
            }
        );
    }

    // Track which source events have been matched
    const matchedSourceIndices = new Set();

    // Result array
    const mergedEvents = [];

    // Process each target event and find overlapping source events
    for (const targetEvent of targetEvents) {
        const overlappingSourceEvents = [];

        sourceEvents.forEach((sourceEvent, index) => {
            // Check if events overlap (any time range intersection)
            if (
                sourceEvent.start < targetEvent.end &&
                sourceEvent.end > targetEvent.start
            ) {
                overlappingSourceEvents.push(sourceEvent);
                matchedSourceIndices.add(index);
            }
        });

        // Combine text from all overlapping source events
        const originalText = overlappingSourceEvents
            .map((event) => event.text || "")
            .filter((text) => text.length > 0)
            .join(" ");

        // Create new Event with merged data
        mergedEvents.push(
            {...(new Event({
                    start: targetEvent.start,
                    end: targetEvent.end,
                    text: targetEvent.text || "",
                    original: originalText,
                }))
            }
        );
    }

    // Add any unmatched source events (with empty text property)
    sourceEvents.forEach((sourceEvent, index) => {
        if (!matchedSourceIndices.has(index)) {
            mergedEvents.push(
                {...(new Event({
                        start: sourceEvent.start,
                        end: sourceEvent.end,
                        text: "",
                        original: sourceEvent.text || "",
                    }))
                }
            );
        }
    });

    // Sort by start time to maintain chronological order
    mergedEvents.sort((a, b) => a.start - b.start);

    return mergedEvents;
}
